import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase";
import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setOtpData } from "store/actions/Authentication/otp";
import { showToast } from "utils/showToast";

const COOLDOWN_TIME = 30;

export const usePhoneVerification = (phone, formData) => {
  const [phoneVerificationError, setPhoneVerificationError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const dispatch = useDispatch();
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const createRecaptchaVerifier = () => {
    recaptchaVerifierRef.current = new RecaptchaVerifier(
      auth,
      recaptchaContainerRef.current,
      {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          setPhoneVerificationError("reCAPTCHA has expired. Please try again.");
        },
      }
    );
  };

  useEffect(() => {
    recaptchaContainerRef.current = document.createElement("div");
    recaptchaContainerRef.current.id = "recaptcha-container";
    document.body.appendChild(recaptchaContainerRef.current);
    createRecaptchaVerifier();

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
      if (recaptchaContainerRef.current) {
        document.body.removeChild(recaptchaContainerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (cooldown > 0) {
      interval = setInterval(() => {
        setCooldown((prevCooldown) => prevCooldown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [cooldown]);

  const resendOTPHandler = useCallback(
    async (e) => {
      e.preventDefault();

      if (cooldown > 0) {
        setPhoneVerificationError(
          `Please wait ${cooldown} seconds before requesting a new OTP.`
        );
        return;
      }

      setPhoneVerificationError(null);

      try {
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phone,
          recaptchaVerifierRef.current
        );
        dispatch(
          setOtpData({
            result: confirmationResult,
            formData: formData,
            phone: phone,
          })
        );

        showToast("OTP resent successfully", "success", true);
        setCooldown(COOLDOWN_TIME);
      } catch (error) {
        console.error("Error resending OTP:", error);
        setPhoneVerificationError("Failed to resend OTP. Please try again.");
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          createRecaptchaVerifier();
        }
      }
    },
    [cooldown, phone, formData, dispatch]
  );

  return { resendOTPHandler, phoneVerificationError, cooldown };
};
